import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import EditReceiptDialog from '../../../../components/audit/EditReceiptDialog/EditReceiptDialog';
import FixOcrFormPanel from '../../../../components/audit/FixOcrFormPanel';
import FixOcrImagePanel from '../../../../components/audit/FixOcrImagePanel';
import AuditReportDialog from '../../../../components/audit/AuditReportDialog';
import SaveConfirmationDialog from '../../../../components/audit/SaveConfirmationDialog';
import ReceiptNotesDialog from '../../../../components/audit/ReceiptNotesDialog';
import useFixOcrPageLogic from '../hooks/useFixOcrPageLogic';
import ErrorBoundary from '../../../../components/common/ErrorBoundary';
import { getVendingShiftReportImageLineCount } from '../utils/vendingShiftReportAlignment';

const FixOCRNewPage: React.FC = () => {
  const guideContainerRef = useRef<HTMLDivElement>(null);
  const guideGridOriginElementRef = useRef<HTMLElement | null>(null);
  const [sharedGuideTop, setSharedGuideTop] = useState<number | null>(null);
  const [shiftGuideGridOriginClientY, setShiftGuideGridOriginClientY] = useState<number | null>(null);
  const {
    queueId,
    storeIdForReceipt,
    storeName,
    reportType,
    imageUrl,
    loading,
    saving,
    savingAction,
    error,
    formData,
    apiData,
    editMode,
    formKey,
    auditStatus,
    editReceiptDialogOpen,
    auditReportDialogOpen,
    auditReport,
    auditReportLoading,
    revertingAudit,
    saveConfirmationOpen,
    rejectNotesDialogOpen,
    rejectNotesSaving,
    pendingSaveData,
    pendingInvalidGames,
    pendingInvalidPacks,
    validatingGamesPacks,
    simulationLoading,
    simulationError,
    simulationResult,
    vendingInventoryComparison,
    varianceByGameId,
    gamesData,
    storeLocations,
    hoveredRowIndex,
    gamesMapState,
    formRef,
    onSubmitClickHandler,
    handleJsonOnChange,
    handleFormDataUpdate,
    handleSwitchModes,
    handleEditReceiptClick,
    handleEditReceiptClose,
    handleEditReceiptSuccess,
    handleConfirmSave,
    handleConfirmRejectWithNote,
    handleCloseAuditReport,
    handleRevertAudit,
    handleCancelSave,
    handleRevertToReady,
    handleAuditBreadcrumbClick,
    onRowHover,
    handleRefreshSimulation,
  } = useFixOcrPageLogic();
  const isVendingShiftReport = reportType === 'vending_shift_report';

  const handleSharedGuideClientYChange = useCallback((clientY: number | null) => {
    if (clientY === null) {
      setSharedGuideTop(null);
      return;
    }

    const containerRect = guideContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    setSharedGuideTop(clientY - containerRect.top);
  }, []);
  const handleSharedGuideElementHover = useCallback((element: HTMLElement | null) => {
    if (!element) {
      setSharedGuideTop(null);
      return;
    }

    const containerRect = guideContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const rowRect = element.getBoundingClientRect();
    setSharedGuideTop(rowRect.bottom - containerRect.top);
  }, []);
  const updateShiftGuideGridOrigin = useCallback(() => {
    if (!guideGridOriginElementRef.current) {
      setShiftGuideGridOriginClientY(null);
      return;
    }

    setShiftGuideGridOriginClientY(guideGridOriginElementRef.current.getBoundingClientRect().bottom);
  }, []);
  const handleGuideGridOriginElementChange = useCallback((element: HTMLElement | null) => {
    guideGridOriginElementRef.current = element;
    if (!element) {
      setShiftGuideGridOriginClientY(null);
      return;
    }

    setShiftGuideGridOriginClientY(element.getBoundingClientRect().bottom);
  }, []);

  useEffect(() => {
    if (!isVendingShiftReport) {
      setShiftGuideGridOriginClientY(null);
      return undefined;
    }

    updateShiftGuideGridOrigin();
    window.addEventListener('resize', updateShiftGuideGridOrigin);
    window.addEventListener('scroll', updateShiftGuideGridOrigin, true);
    return () => {
      window.removeEventListener('resize', updateShiftGuideGridOrigin);
      window.removeEventListener('scroll', updateShiftGuideGridOrigin, true);
    };
  }, [isVendingShiftReport, formKey, loading, updateShiftGuideGridOrigin]);

  if (!queueId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No queue ID provided</Alert>
      </Box>
    );
  }

  const shiftSalesLineCount = formData?.parsed_data?.final_json?.sales?.lines?.length || 0;
  const shiftInventoryLineCount = formData?.parsed_data?.final_json?.inventory?.lines?.length || 0;
  const ticketCount = reportType === 'vending_shift_report'
    ? getVendingShiftReportImageLineCount(shiftSalesLineCount, shiftInventoryLineCount)
    : (reportType === 'vending_sales' || reportType === 'vending_inventory'
      ? (formData?.parsed_data?.final_json?.lines?.length || 0)
      : (formData?.ticket_details?.length || 0));

  return (
    <Box sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          <Box ref={guideContainerRef} sx={{ position: 'relative' }}>
            <Grid container sx={{ flex: 1, marginTop: 0.5, alignItems: 'stretch' }} columnSpacing={2}>
              <Grid size={{ xs: 12, md: 3, lg: 3 }} sx={{ minWidth: '590px' }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                  <Link
                    color="rgba(44, 108, 160, 1)"
                    component="button"
                    type="button"
                    onClick={handleAuditBreadcrumbClick}
                    sx={{ cursor: 'pointer' }}
                  >
                    Audit
                  </Link>
                  <Typography color="text.primary" fontWeight={600}>
                    Fix OCR
                  </Typography>
                </Breadcrumbs>

                <FixOcrFormPanel
                  reportType={reportType}
                  editMode={editMode}
                  formData={formData}
                  apiData={apiData}
                  formKey={formKey}
                  formRef={formRef}
                  queueId={queueId}
                  gamesMapState={gamesMapState}
                  auditStatus={auditStatus}
                  saving={saving}
                  savingAction={savingAction}
                  onSubmitClickHandler={onSubmitClickHandler}
                  onFormDataUpdate={handleFormDataUpdate}
                  onSwitchModes={handleSwitchModes}
                  handleJsonOnChange={handleJsonOnChange}
                  hoveredRowIndex={hoveredRowIndex}
                  onRowHover={onRowHover}
                  onGuideLineElementHover={isVendingShiftReport ? handleSharedGuideElementHover : undefined}
                  onGuideGridOriginElementChange={isVendingShiftReport ? handleGuideGridOriginElementChange : undefined}
                  storeName={storeName}
                  onRevertToReady={handleRevertToReady}
                  varianceByGameId={varianceByGameId}
                  onRefreshSimulation={handleRefreshSimulation}
                  simulationLoading={simulationLoading}
                  simulationError={simulationError}
                  vendingInventoryNewPackKeys={vendingInventoryComparison?.newPackKeys}
                  vendingInventoryCompletedPacks={vendingInventoryComparison?.completedPacks}
                  storeLocations={storeLocations}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', alignSelf: 'stretch' }}>
                <ErrorBoundary
                  resetKeys={[queueId, imageUrl]}
                  fallback={(
                    <Box sx={{ p: 2 }}>
                      <Alert severity="warning">
                        Receipt image panel failed to load. You can still edit the form and submit the audit.
                      </Alert>
                    </Box>
                  )}
                >
                  <FixOcrImagePanel
                    imageUrl={imageUrl}
                    loading={loading}
                    saving={saving}
                    ticketCount={ticketCount}
                    reportType={reportType}
                    onEditReceipt={handleEditReceiptClick}
                    hoveredRowIndex={hoveredRowIndex}
                    onRowHover={onRowHover}
                    onGuideLineClientYChange={isVendingShiftReport ? handleSharedGuideClientYChange : undefined}
                    showHoverGuideLine
                    showGuideGrid
                    guideGridOriginClientY={isVendingShiftReport ? shiftGuideGridOriginClientY : undefined}
                    clientMetadata={apiData?.client_metadata}
                  />
                </ErrorBoundary>
              </Grid>
            </Grid>
            {isVendingShiftReport && sharedGuideTop !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  top: `${sharedGuideTop}px`,
                  left: 0,
                  right: 0,
                  height: '2px',
                  bgcolor: '#000000',
                  pointerEvents: 'none',
                  zIndex: 30,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 0 8px rgba(58, 49, 40, 0.35)',
                }}
              />
            )}
          </Box>
        </>
      )}

      <EditReceiptDialog
        open={editReceiptDialogOpen}
        onClose={handleEditReceiptClose}
        onSuccess={handleEditReceiptSuccess}
        queueId={queueId}
        storeId={storeIdForReceipt}
        reportType={reportType}
        formData={formData}
      />

      <SaveConfirmationDialog
        open={saveConfirmationOpen}
        onCancel={handleCancelSave}
        onConfirm={handleConfirmSave}
        saving={saving}
        invalidGames={pendingInvalidGames}
        invalidPacks={pendingInvalidPacks}
        gamesData={gamesData}
        validating={validatingGamesPacks}
        simulationLoading={simulationLoading}
        simulationError={simulationError}
        simulationResult={simulationResult}
        action={pendingSaveData?.action}
      />

      <ReceiptNotesDialog
        open={rejectNotesDialogOpen}
        title="Reject Receipt"
        mode="reject"
        reviewNotes={apiData?.review_notes || apiData?.notes || null}
        saving={rejectNotesSaving || saving}
        onClose={handleCancelSave}
        onReject={handleConfirmRejectWithNote}
      />

      <AuditReportDialog
        open={auditReportDialogOpen}
        onClose={handleCloseAuditReport}
        onRevert={handleRevertAudit}
        report={auditReport}
        loading={auditReportLoading}
        reverting={revertingAudit}
      />
    </Box>
  );
};

export default FixOCRNewPage;
